import React, { createContext, FC, useEffect, useState } from 'react'
import { isPlatform } from '@ionic/react'
import { Preferences } from '@capacitor/preferences'
import { ApolloClient, NormalizedCacheObject } from '@apollo/client'

import { ApolloProvider as ApolloClientProvider } from '@apollo/client/react'

import { authLink, cache, httpLink } from '@src/global'
import { Overlay } from '@src/components/Elements/Loading'
import { CachePersistor, PersistentStorage } from 'apollo3-cache-persist'

interface ApolloContextProps {
  client?: ApolloClient
  persistor: CachePersistor<any>
}

interface ApolloProviderProps {
  children?: React.ReactNode
}

export const ApolloContext = createContext<ApolloContextProps>({
  client: new ApolloClient({
    cache,
    link: authLink,
  }),
  persistor: (() => Promise.reject('CachePersistor not initialized')) as any,
})

export const ApolloProvider: FC<ApolloProviderProps> = ({ children }) => {
  const [isCachedInitialized, setIsCachedInitialized] = useState<boolean>()

  // Capacitor Storage
  const storage: PersistentStorage<any> = {
    getItem: async (key) => Preferences.get({ key }),
    setItem: async (key, value) => Preferences.set({ key, value }),
    removeItem: async (key) => Preferences.remove({ key }),
  }

  // Cache-Persistor
  const persistorKey = 'apollo-cache-persist'
  const persistor = new CachePersistor({ cache, storage, key: persistorKey, debug: false })

  const client = new ApolloClient({
    cache,
    link: authLink.concat(httpLink),

    // Disable on hybrid platforms.
    queryDeduplication: !isPlatform('hybrid'),
  })

  // Manual cache restorage as the persistor.restore() is restoring just empty content. Queries will be refetched
  // automatically afterwards.
  useEffect(() => {
    if (!isCachedInitialized) {
      Preferences.get({ key: persistorKey }).then(({ value }) => {
        if (value) {
          const myCache: NormalizedCacheObject = JSON.parse(value)
          cache.restore(myCache)
        }

        setIsCachedInitialized(true)
      })
    }
  }, [isCachedInitialized])

  if (isCachedInitialized) {
    return (
      <ApolloContext.Provider value={{ client, persistor }}>
        <ApolloClientProvider client={client}>{children}</ApolloClientProvider>
      </ApolloContext.Provider>
    )
  }

  return <Overlay />
}
